import { Vue, Component } from "vue-property-decorator";
import { namespace, Mutation, Action, Getter } from "vuex-class";
import { get } from "lodash";
import { FeaturesList } from "@/store/types";
import { formatRegionUrl } from "@/utils/string-utils";
import api from "@/services/api.service";
import { FilteredEvent } from "@/store/eventqueue/types";

const EventQueue = namespace("eventqueue");
const SiteMonitor = namespace("siteMonitor");
const Regions = namespace("regions");

@Component
export default class RegionalHandlerMixin extends Vue {
	@Getter("getFeaturesList") public featuresList: FeaturesList;
	@Getter("getDbFederationEnabled") public dbFederationEnabled: boolean;

	@Regions.Action public loginToRegion: ({ regionId, regionUrl, overwriteLocalLogin }) => Promise<any>;

	@EventQueue.Action public handleEvent: ({ eventID, checkInProcessing, regionId, regionUrl }) => Promise<void>;

	@SiteMonitor.Action public updateEventRegion: ({ regionId, regionUrl }) => Promise<void>;
	@SiteMonitor.Action public resetRegion: () => Promise<void>;

	/**
	 * Attempts to login to a remote region, firstly generating a login token, and then switching our Axios instance
	 * over to point at the DataService on the remote server.
	 * @param regionId The ID of the region to login to.
	 * @param regionUrl The URL of the region to login to.
	 */
	public async regionalLogin(regionId: number, regionUrl: string) {
		try {
			// Login to the remote region using the loginToken
			const loginResult = await this.loginToRegion({ regionId, regionUrl, overwriteLocalLogin: true });

			if (typeof loginResult !== "undefined" && loginResult != null) {
				// If we've successfully logged in, we can return true and carry on with whatever we're doing
				return true;
			} else {
				throw new ReferenceError("Login Result was undefined or null");
			}
		} catch (err) {
			console.error(`Could not login to remote region (ID: ${regionId}, URL: ${regionUrl}): ${err}`);

			// reset our region back to the local one
			this.resetRegion();
		}

		return false;
	}

	/**
	 * Handles an event queue item, taking into account handling regional login etc.
	 * @param eventQueueItem The item from the Event Queue to be handled.
	 */
	public async handleEventQueueItem(
		eventQueueItem: { eventID; regionID; regionURL },
		checkInProcessing: boolean = false
	) {
		if (!get(this.featuresList, ["Alarms", "SiteMonitor"])) {
			return;
		}

		let { eventID, regionID, regionURL } = eventQueueItem;

		if (typeof regionURL === "undefined" || regionURL == null || regionURL.trim() === "" || regionID <= 0) {
			// No region shenanigans here, so just process the event
			try {
				await this.handleEvent({
					eventID,
					checkInProcessing,
					regionId: 0,
					regionUrl: null
				});
			} catch (err) {
				// Swallow err
			}
		} else {
			// Attempt to login to the remote region
			regionURL = formatRegionUrl(regionURL);
			const loginSuccessful = await this.regionalLogin(regionID, regionURL);

			if (loginSuccessful) {
				try {
					await this.handleEvent({
						eventID,
						checkInProcessing,
						regionId: regionID,
						regionUrl: regionURL
					});
				} catch (err) {
					// Couldn't handle the event, so we need to reset our region
					this.resetRegion();
				}
			} else {
				this.$notify({
					type: "error",
					title: "Unsuccessful Remote Login",
					text:
						"Unable to login to the remote region - please try again later, or contact support if the problem persists."
				});
			}
		}
	}

	public dispatchedGuardCallsigns(dispatchedGuards: any[]): string {
		if (!dispatchedGuards) {
			return "";
		}

		return dispatchedGuards.map(guard => guard.callsign).join(", ");
	}
}
